import { type Swiper as SwiperType } from 'swiper'
import { FreeMode, Mousewheel } from 'swiper/modules'
import { Swiper as SwiperComponent, SwiperSlide } from 'swiper/react'

import { useProjectList } from '@/api/models/main'
import { AppCheckboxBadged } from '@/components/shared/checkbox'
import { useCheckboxListSwiper } from '@/components/shared/checkbox/hooks/useCheckboxListSwiper'
import { checkboxItemAll } from '@/components/shared/checkbox/lib/constants'
import {
  getModifiedCheckboxList,
  isCheckboxItemChecked
} from '@/components/shared/checkbox/lib/utils'

import { projectOptionAll } from '../lib/constants'
import { useUserContactsStore } from '../model/store'

type Props = {
  className: string
  onFilterChange: () => void
}

const UserContactsFilter = ({ className, onFilterChange }: Props) => {
  const { selectedProjects, setSelectedProjects } = useUserContactsStore()

  const { swiperRef } = useCheckboxListSwiper({
    selectedItems: selectedProjects
  })

  const { data: projectList = [] } = useProjectList()

  if (projectList.length <= 1) {
    return <div className="h-20" aria-hidden="true" />
  }

  const projectOptions = [projectOptionAll, ...projectList].map(
    ({ bc_guid, bc_name }) => ({
      value: bc_guid,
      label: bc_name,
      checked: isCheckboxItemChecked(bc_guid, selectedProjects)
    })
  )

  const onCheckboxChange = (target: { value: string; checked: boolean }) => {
    const updatedCheckboxList = getModifiedCheckboxList(
      target,
      projectOptions,
      selectedProjects
    )
    setSelectedProjects(updatedCheckboxList)
    const isAllSelected = updatedCheckboxList?.length === undefined

    if (swiperRef.current && isAllSelected) {
      swiperRef.current?.slideTo(0, 100)
    }
    onFilterChange()
  }

  return (
    <SwiperComponent
      modules={[FreeMode, Mousewheel]}
      slidesPerView="auto"
      spaceBetween={8}
      mousewheel
      watchOverflow
      threshold={10}
      observer={true}
      observeParents={true}
      className={className}
      onInit={(swiper: SwiperType) => (swiperRef.current = swiper)}
    >
      {projectOptions.map(option => (
        <SwiperSlide key={option.value} className="!w-auto">
          <AppCheckboxBadged
            {...option}
            closable={option.value !== checkboxItemAll}
            onChange={({ target }) => onCheckboxChange(target)}
          />
        </SwiperSlide>
      ))}
    </SwiperComponent>
  )
}

export default UserContactsFilter
